import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, CardTitle } from "reactstrap";
import ComplianceMonitoringDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/DashboardService";
import ChartError from "src/modules/3rd-party-management/pages/admin/compliance/components/dashboard/ChartError";

const MonthKeys = {
    "1": "Jan",
    "2": "Feb",
    "3": "Mar",
    "4": "Apr",
    "5": "May",
    "6": "Jun",
    "7": "Jul",
    "8": "Aug",
    "9": "Sep",
    "10": "Oct",
    "11": "Nov",
    "12": "Dec"
}

const MonthlyMonitoredEntites = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-monitoring-dashboard",
            "monitored-by-month",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringDashboardService.getInstance();

            return await service.fetchMonthlyMonitors(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        }
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '20%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        months = []
    } = fetchDataQuery.data;

    const labels = [];
    const entitiesAmounts = [];
    const individualAmounts = [];

    months.forEach(element => {
        labels.push(
            MonthKeys[parseInt((element.month || '').split('-')?.[1] || '0')?.toString()]
        );

        entitiesAmounts.push(element.entities || 0);
        individualAmounts.push(element.individuals || 0);
    });

    const amounts = [
        {
            name: "Entities",
            data: entitiesAmounts
        },
        {
            name: "Individual",
            data: individualAmounts
        }
    ];

    const colors = ["#5664D2", "#1CBB8C"]; // blue, green

    return (
        <Card className="h-100">
            <CardBody>
                <div className="mb-4">
                    <CardTitle>
                        {t('Monitored Entities And Individuals / Month')}
                    </CardTitle>
                </div>
                <div className="mb-3">
                    {fetchDataQuery.isError ? (
                        <ChartError
                            title={
                                fetchDataQuery.error?.response?.status === 403 ?
                                    'Error 403' :
                                    null
                            }
                            message={
                                fetchDataQuery.error?.response?.status === 403 ?
                                    'Please upgrade your plan to access this feature' :
                                    null
                            }
                            onRetry={fetchDataQuery.refetch}
                        />
                    ) : (
                        <ReactApexChart
                            options={{
                                colors: colors,
                                legend: {
                                    show: true,
                                },
                                chart: {
                                    toolbar: {
                                        show: false,
                                    },
                                },
                                plotOptions: {
                                    bar: {
                                        horizontal: false,
                                        distributed: false
                                    },
                                },
                                dataLabels: {
                                    enabled: true,
                                    position: "center",
                                    style: {
                                        fontSize: "12px",
                                    },
                                    formatter: function (val) {
                                        return val;
                                    },
                                },
                                xaxis: {
                                    position: "bottom",
                                    categories: labels,
                                    axisBorder: {
                                        show: true,
                                    },
                                    axisTicks: {
                                        show: false,
                                    },
                                    labels: {
                                        formatter: (val) => {
                                            const splitedVal = val.toString().split(' ')
                                            return `${t(splitedVal[0])} ${t(splitedVal[1])}`;
                                        }
                                    }
                                },
                                yaxis: {
                                    show: true,
                                    labels: {
                                        formatter: (val) => {
                                            return val.toFixed(0)
                                        }
                                    }
                                },
                                tooltip: {
                                    theme: "light",
                                    enabled: true,
                                    followCursor: true,
                                }
                            }}
                            series={amounts}
                            type="bar"
                            height="230px"
                        />
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(
    MonthlyMonitoredEntites
)